import React, { useState, useEffect, useRef } from 'react'
import toast from 'react-hot-toast'
import { useSearchStore, type SortOption } from '../../../store/jpeTopPageStore'
import { FilterBadge } from '../search-elements/FilterBadge'
import { getFilterLabel, copyToClipboard, type FilterSection } from '../utils'

// Re-export for convenience
export type { SortOption, FilterSection }

interface SortPopoverProps {
  isOpen: boolean
  onClose: () => void
  anchorRef: React.RefObject<HTMLButtonElement | null>
}

const SORT_OPTIONS = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'lowest_price', label: 'Lowest Price' },
  { value: 'near_airport', label: 'Near Airport' },
]

// key は FilterModal がスクロール先の節を引く値。「Preferences」は kitchen / bathroom が同居する
// 「Accommodation Preferences」の節なので、その先頭の kitchen を指す
const FILTER_OPTIONS: Array<{ key: FilterSection; label: string }> = [
  { key: 'mobilityConditions', label: 'Car-free' },
  { key: 'mealService', label: 'School Lunch' },
  { key: 'nurseryFeatures', label: 'Features' },
  { key: 'roomType', label: 'Accommodation' },
  { key: 'kitchen', label: 'Preferences' },
]

const SortPopover: React.FC<SortPopoverProps> = ({ isOpen, onClose, anchorRef }) => {
  const { selectedSort, setSelectedSort } = useSearchStore()
  const popoverRef = useRef<HTMLDivElement>(null)

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose, anchorRef])

  if (!isOpen) return null

  const handleOptionClick = (option: SortOption) => {
    setSelectedSort(option)
    onClose()
  }

  return (
    <div
      ref={popoverRef}
      className="absolute z-50 mt-1 bg-white border border-gray-200 shadow-lg"
      style={{
        minWidth: '170px',
        borderRadius: '20px',
        left: anchorRef.current?.offsetLeft || '-10px',
        top: (anchorRef.current?.offsetTop || 0) + (anchorRef.current?.offsetHeight || 0),
      }}
    >
      <div className="py-2">
        {SORT_OPTIONS.map((option) => (
          <button
            key={option.value}
            className="w-full flex items-center justify-between px-4 py-2 hover:bg-gray-50 text-sm text-left"
            onClick={() => handleOptionClick(option.value as SortOption)}
          >
            <span className="text-gray-900">{option.label}</span>
            {selectedSort === option.value && (
              <div className="ml-2">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M6.00016 10.7799L3.22016 7.99994L2.27344 8.93994L6.00016 12.6666L14.0002 4.66661L13.0602 3.72661L6.00016 10.7799Z"
                    fill="#111827"
                  />
                </svg>
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

export const SortFilterBar: React.FC = () => {
  const { filterData, removeFilter, totalCount, resultCount, setScrollToSection, setFilterModalOpen, generateShareUrl } =
    useSearchStore()
  const [showSortPopover, setShowSortPopover] = useState(false)
  const sortButtonRef = useRef<HTMLButtonElement>(null)

  // Collect all active filter badges
  const getActiveFilterBadges = () => {
    if (!filterData) return []

    const badges: Array<{ section: FilterSection; value: string; label: string }> = []

    // Process each filter section
    const sections: FilterSection[] = ['mobilityConditions', 'mealService', 'nurseryFeatures', 'roomType', 'kitchen', 'bathroom']

    sections.forEach((section) => {
      const values = filterData[section] as string[]
      if (values && values.length > 0) {
        values.forEach((value) => {
          const label = getFilterLabel(section, value)
          if (label && !badges.some((b) => b.label === label)) {
            badges.push({ section, value, label })
          }
        })
      }
    })

    return badges
  }

  const activeFilterBadges = getActiveFilterBadges()
  const hasActiveFilters = activeFilterBadges.length > 0

  const handleOpenFilter = (section?: FilterSection) => {
    setScrollToSection(section)
    setFilterModalOpen(true)
  }

  const handleShareClick = async () => {
    const shareUrl = generateShareUrl()
    try {
      await copyToClipboard(shareUrl)
      toast.success('Link copied to clipboard!', {
        duration: 3000,
        position: 'top-center',
        style: {
          background: '#10B981',
          color: '#fff',
          padding: '12px 24px',
          borderRadius: '8px',
        },
      })
    } catch (_error) {
      toast.error('Failed to copy link', {
        duration: 3000,
        position: 'top-center',
      })
    }
  }

  const handleSortClick = () => {
    setShowSortPopover(!showSortPopover)
  }

  const handleRemoveFilter = (section: FilterSection, value: string) => {
    removeFilter(section, value)
  }

  return (
    <>
      {/* Drag Handle */}
      <div className="w-full py-2 sm:py-3 flex justify-center">
        <div className="w-12 rounded-full" style={{ height: '3px', backgroundColor: '#6B7280' }}></div>
      </div>

      {/* Filter Section */}
      <div className="px-3 pb-2 sm:px-4 sm:pb-3">
        {/* Filter Buttons Row */}
        <div className="flex items-center gap-2 mb-2 sm:mb-3 relative max-h-[25vh] sm:max-h-[20vh]">
          <button
            ref={sortButtonRef}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
            onClick={handleSortClick}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M8 13V5.825L5.425 8.4L4 7L9 2L14 7L12.575 8.4L10 5.825V13H8ZM15 22L10 17L11.425 15.6L14 18.175V11H16V18.175L18.575 15.6L20 17L15 22Z"
                fill="black"
              />
            </svg>
          </button>
          <div className="flex items-center overflow-auto gap-2">
            {FILTER_OPTIONS.map(({ key, label }) => (
              <button
                key={key}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm shrink-0"
                onClick={() => handleOpenFilter(key)}
              >
                {label}
                <svg width="10" height="5" viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 0L5 5L10 0H0Z" fill="#111827" />
                </svg>
              </button>
            ))}
          </div>

          <SortPopover isOpen={showSortPopover} onClose={() => setShowSortPopover(false)} anchorRef={sortButtonRef} />
        </div>

        {/* Active Filter Badges */}
        {hasActiveFilters && (
          <div className="mt-2 sm:mt-3 overflow-auto max-h-[15vh]">
            <div className="flex gap-2 pb-1 flex-nowrap">
              {activeFilterBadges.map((badge) => (
                <FilterBadge
                  key={`${badge.section}-${badge.value}`}
                  section={badge.section}
                  value={badge.value}
                  label={badge.label}
                  onRemove={handleRemoveFilter}
                />
              ))}
            </div>
          </div>
        )}

        {/* Results Count and Share Link */}
        <div className="flex items-center justify-between text-xs sm:text-sm mt-2">
          <span className="text-black-600">
            {resultCount} of {totalCount} results
          </span>
          <button className="flex items-center gap-1 text-gray-600 underline" onClick={handleShareClick}>
            <svg width="15" height="17" viewBox="0 0 15 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M5 13.3333C4.54167 13.3333 4.14931 13.1701 3.82292 12.8437C3.49653 12.5174 3.33333 12.125 3.33333 11.6667V1.66667C3.33333 1.20833 3.49653 0.815972 3.82292 0.489583C4.14931 0.163194 4.54167 0 5 0H12.5C12.9583 0 13.3507 0.163194 13.6771 0.489583C14.0035 0.815972 14.1667 1.20833 14.1667 1.66667V11.6667C14.1667 12.125 14.0035 12.5174 13.6771 12.8437C13.3507 13.1701 12.9583 13.3333 12.5 13.3333H5ZM5 11.6667H12.5V1.66667H5V11.6667ZM1.66667 16.6667C1.20833 16.6667 0.815972 16.5035 0.489583 16.1771C0.163194 15.8507 0 15.4583 0 15V3.33333H1.66667V15H10.8333V16.6667H1.66667Z"
                fill="#6B7280"
              />
            </svg>
            Filtered shareable link
          </button>
        </div>
      </div>
    </>
  )
}
